/**
 * toastSlice: 전역 토스트 및 알림 상태 관리
 *
 * - notifications: 상단 알림 카드 (커스텀 버튼/텍스트 지원)
 * - toasts: 우하단 채팅형 토스트 (자동 소멸)
 *
 * 버튼 콜백은 직렬화 불가하므로 Redux에 저장하지 않고
 * globalToast.ts의 callbackRegistry에서 관리한다.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  NotificationButton,
  GlobalNotification,
  GlobalToastMessage,
  SnackbarMessage,
  SnackbarQueueItem,
} from '@/types/store';

// re-export for backwards compatibility
export type {
  NotificationButton,
  GlobalNotification,
  GlobalToastMessage,
  SnackbarMessage,
  SnackbarQueueItem,
} from '@/types/store';

export interface ToastState {
  notifications: GlobalNotification[];
  toasts: GlobalToastMessage[];
  snackbar: SnackbarMessage;
  snackbarQueue: SnackbarQueueItem[];
}

// ─── 초기 상태 ────────────────────────────────────────────────────────────

let snackbarIdCounter = 0;

const initialState: ToastState = {
  notifications: [],
  toasts: [],
  snackbar: { open: false, type: 'info', message: '', seq: 0 },
  snackbarQueue: [],
};

// ─── 슬라이스 ─────────────────────────────────────────────────────────────

const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    addNotification(state, action: PayloadAction<Omit<GlobalNotification, 'timestamp'>>) {
      state.notifications.push({
        ...action.payload,
        timestamp: Date.now(),
      });
    },

    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },

    clearNotifications(state) {
      state.notifications = [];
    },

    addToast(state, action: PayloadAction<Omit<GlobalToastMessage, 'timestamp'>>) {
      state.toasts.push({
        ...action.payload,
        timestamp: Date.now(),
      });
    },

    removeToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },

    clearToasts(state) {
      state.toasts = [];
    },

    /** 전역 SNSnackbar 표시 — 어디서든 dispatch 가능 */
    showSnackbar(state, action: PayloadAction<{ type: 'success' | 'error' | 'info'; message: string }>) {
      state.snackbar = { open: true, ...action.payload, seq: state.snackbar.seq + 1 };
    },

    closeSnackbar(state, action: PayloadAction<number | undefined>) {
      // seq가 전달된 경우, 현재 seq와 일치할 때만 닫는다.
      // 오래된 타이머가 새로 열린 스낵바를 닫는 race condition을 방지.
      if (action.payload !== undefined && action.payload !== state.snackbar.seq) return;
      state.snackbar.open = false;
    },

    /** 스낵바 큐에 추가 — 여러 개를 동시에 표시할 때 사용 */
    enqueueSnackbar(state, action: PayloadAction<{ type: 'success' | 'error' | 'info'; message: string }>) {
      state.snackbarQueue.push({
        id: ++snackbarIdCounter,
        ...action.payload,
      });
    },

    removeSnackbar(state, action: PayloadAction<number>) {
      state.snackbarQueue = state.snackbarQueue.filter((s) => s.id !== action.payload);
    },

    reset() {
      return initialState;
    },
  },
});

export const toastActions = toastSlice.actions;
export default toastSlice.reducer;
